// src/pages/BrandsHubPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";
import UserActionsBar from "../components/UserActionsBar";
import ConfirmDialog from "../components/ConfirmDialog";
import BrandOnboardingModal from "../components/BrandOnboardingModal";
import Toast from "../components/Toast";

import {
  fetchBrandHub,
  brandHubCreate,
  brandHubRename,
  brandHubSetMembership,
  brandHubDelete,
  fetchBrandAliases,
  addBrandAlias,
  deleteBrandAlias,
  analysisRunsActiveByBrand,
} from "../services/api";

// LLM178: inline chip + popover showing non-terminal runs for a brand.
// Click toggles the popover; clicking a run navigates into the wizard.
function ActiveRunsChip({ count, runs, open, onToggle, onClose, onOpenRun }) {
  const wrapRef = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  const hoverTitle =
    (runs || [])
      .map((r) => `#${r.id} — ${r.title || r.status}${r.assignee_name ? ` (${r.assignee_name})` : ""}`)
      .join("\n") || "";

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        onClick={onToggle}
        title={hoverTitle}
        style={{
          padding: "2px 8px",
          borderRadius: 999,
          border: "1px solid #bfdbfe",
          background: "#eff6ff",
          color: "#1e3a8a",
          fontSize: 11,
          fontWeight: 800,
          cursor: "pointer",
          lineHeight: 1.2,
        }}
      >
        ● {count} active
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            minWidth: 260,
            maxWidth: 360,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            boxShadow: "0 12px 32px rgba(15,23,42,0.18)",
            padding: 8,
            zIndex: 100,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", padding: "4px 8px" }}>
            Active runs
          </div>
          {(runs || []).map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onOpenRun(r.id)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "8px 10px",
                background: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 12,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
            >
              <div style={{ fontWeight: 800, color: "#111827" }}>
                {r.title || `Run #${r.id}`}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                {r.status.replace(/_/g, " ")}
                {r.assignee_name ? ` · ${r.assignee_name}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}


function Toggle({ checked, onChange, disabled, label }) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(!!e.target.checked)}
        disabled={disabled}
      />
      <span style={{ fontWeight: 500, fontSize: 13, color: "#111827" }}>
        {label}
      </span>
    </label>
  );
}

function SortArrow({ column, sortCol, sortDir }) {
  if (sortCol !== column) {
    return (
      <span style={{ opacity: 0.3, marginLeft: 4, fontSize: 10 }}>
        {"  \u2195"}
      </span>
    );
  }
  return (
    <span style={{ marginLeft: 4, fontSize: 10 }}>
      {sortDir === "asc" ? "\u25B2" : "\u25BC"}
    </span>
  );
}

export default function BrandsHubPage({ currentUser }) {
  const nav = useNavigate();
  const userLabel = currentUser?.full_name || currentUser?.email || "User";
  const isAdmin = !!(currentUser?.is_admin || currentUser?.role === "admin");

  const [toast, setToast] = useState(null);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const [q, setQ] = useState("");

  // Sorting state
  const [sortCol, setSortCol] = useState("id");
  const [sortDir, setSortDir] = useState("asc");

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createEnableLlm, setCreateEnableLlm] = useState(true);
  const [createEnableAds, setCreateEnableAds] = useState(false);
  const [creating, setCreating] = useState(false);

  // Edit setup state
  const [editSetupBrandId, setEditSetupBrandId] = useState(null);

  // Rename dialog state
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renaming, setRenaming] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Aliases dialog state
  const [aliasOpen, setAliasOpen] = useState(false);
  const [aliasTarget, setAliasTarget] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [aliasInput, setAliasInput] = useState("");
  const [aliasLoading, setAliasLoading] = useState(false);

  // Membership toggle in-flight tracking
  const [busyId, setBusyId] = useState(null);

  // LLM178: batch active-runs map (brand_id -> {count, runs}). Refreshed
  // on mount and every 30s while the tab is foreground so users see each
  // other's in-flight work without manual reload.
  const [activeByBrand, setActiveByBrand] = useState({});
  const [activePopoverBrandId, setActivePopoverBrandId] = useState(null);

  const showToast = (message, type = "info") => setToast({ message, type });

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchBrandHub(); // returns array (api.js unwraps {items})
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("[BRANDS HUB LOAD ERROR]", e);
      showToast(e?.message || "Failed to load Brands Hub", "error");
    } finally {
      setLoading(false);
    }
  };

  const aliasAutoOpened = React.useRef(false);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // LLM178: fetch + poll active runs per brand. 30s cadence when the tab
  // is foreground; refetch immediately on visibilitychange.
  useEffect(() => {
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const res = await analysisRunsActiveByBrand();
        if (cancelled) return;
        const map = {};
        for (const row of res?.items || []) {
          map[Number(row.brand_id)] = row;
        }
        setActiveByBrand(map);
      } catch {
        // Silent — chip just won't update this tick
      }
    };
    fetchActive();
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchActive();
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchActive();
    }, 30_000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, []);

  // Auto-open aliases dialog if ?aliases=brandId in URL (after items load)
  useEffect(() => {
    if (aliasAutoOpened.current || items.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const aliasId = params.get("aliases");
    if (aliasId) {
      const brand = items.find((b) => b.id === Number(aliasId));
      if (brand) {
        aliasAutoOpened.current = true;
        openAliases(brand);
      }
    }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = useCallback(
    (col) => {
      if (sortCol === col) {
        setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortCol(col);
        setSortDir("asc");
      }
    },
    [sortCol]
  );

  const filtered = useMemo(() => {
    const needle = (q || "").trim().toLowerCase();
    let list = items;
    if (needle) {
      list = list.filter((b) =>
        String(b?.name || "").toLowerCase().includes(needle)
      );
    }

    // Sort
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...list].sort((a, b) => {
      switch (sortCol) {
        case "name": {
          const na = (a.name || "").toLowerCase();
          const nb = (b.name || "").toLowerCase();
          return na < nb ? -dir : na > nb ? dir : 0;
        }
        case "llm": {
          const la = a.modules?.llm ? 1 : 0;
          const lb = b.modules?.llm ? 1 : 0;
          return (la - lb) * dir;
        }
        case "ads": {
          const aa = a.modules?.ads ? 1 : 0;
          const ab = b.modules?.ads ? 1 : 0;
          return (aa - ab) * dir;
        }
        case "id":
        default:
          return (Number(a.id) - Number(b.id)) * dir;
      }
    });

    return sorted;
  }, [items, q, sortCol, sortDir]);

  const openCreate = () => {
    setCreateEnableLlm(true);
    setCreateEnableAds(false);
    setCreateOpen(true);
  };

  const handleCreate = async (brandName) => {
    const name = (brandName || "").trim();
    if (!name) {
      showToast("Enter brand name", "error");
      setCreateOpen(false);
      return;
    }

    setCreating(true);
    try {
      const created = await brandHubCreate({
        name,
        enable_llm: !!createEnableLlm,
        enable_ads: !!createEnableAds,
      });

      // Re-fetch from server to ensure list is in sync
      await load();

      const modText =
        (created?.modules?.llm ? "LLM" : "") +
        (created?.modules?.llm && created?.modules?.ads ? " + " : "") +
        (created?.modules?.ads ? "Ads" : "");

      showToast(
        `Brand "${created?.name || name}" ready (${modText || "none"})`,
        "success"
      );
    } catch (e) {
      console.error("[BRANDS HUB CREATE ERROR]", e);
      showToast(e?.message || "Create failed", "error");
    } finally {
      setCreating(false);
      setCreateOpen(false);
    }
  };

  const openAliases = async (brand) => {
    setAliasTarget(brand);
    setAliasOpen(true);
    setAliasInput("");
    setAliasLoading(true);
    try {
      const data = await fetchBrandAliases(brand.id);
      setAliases(data?.items || []);
    } catch {
      setAliases([]);
    } finally {
      setAliasLoading(false);
    }
  };

  const handleAddAlias = async () => {
    const val = aliasInput.trim();
    if (!val || !aliasTarget) return;
    try {
      await addBrandAlias(aliasTarget.id, val);
      setAliasInput("");
      const data = await fetchBrandAliases(aliasTarget.id);
      setAliases(data?.items || []);
    } catch (e) {
      showToast(e?.message || "Failed to add alias", "error");
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    if (!aliasTarget) return;
    try {
      await deleteBrandAlias(aliasTarget.id, aliasId);
      setAliases((prev) => prev.filter((a) => a.id !== aliasId));
    } catch (e) {
      showToast(e?.message || "Failed to delete alias", "error");
    }
  };

  const openRename = (brand) => {
    setRenameTarget(brand);
    setRenameOpen(true);
  };

  const handleRename = async (newName) => {
    const name = (newName || "").trim();
    if (!renameTarget?.id) {
      setRenameOpen(false);
      return;
    }
    if (!name) {
      showToast("Name cannot be empty", "error");
      setRenameOpen(false);
      return;
    }

    setRenaming(true);
    try {
      const updated = await brandHubRename(renameTarget.id, name);
      setItems((prev) =>
        prev.map((x) =>
          Number(x.id) === Number(updated?.id) ? { ...x, ...updated } : x
        )
      );
      showToast(`Renamed to "${updated?.name || name}"`, "success");
    } catch (e) {
      console.error("[BRANDS HUB RENAME ERROR]", e);
      showToast(e?.message || "Rename failed", "error");
    } finally {
      setRenaming(false);
      setRenameOpen(false);
      setRenameTarget(null);
    }
  };

  const openDelete = (brand) => {
    setDeleteTarget(brand);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) {
      setDeleteOpen(false);
      return;
    }

    setDeleting(true);
    try {
      await brandHubDelete(deleteTarget.id);
      setItems((prev) =>
        prev.filter((x) => Number(x.id) !== Number(deleteTarget.id))
      );
      showToast(
        `Brand "${deleteTarget.name}" deleted`,
        "success"
      );
    } catch (e) {
      console.error("[BRANDS HUB DELETE ERROR]", e);
      showToast(e?.message || "Delete failed", "error");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const setMembership = async (brand, module, enabled) => {
    const id = Number(brand?.id);
    if (!id) return;

    setBusyId(id);
    try {
      const updated = await brandHubSetMembership(id, module, enabled);
      setItems((prev) =>
        prev.map((x) => (Number(x.id) === id ? { ...x, ...updated } : x))
      );
      showToast(
        `Brand "${updated?.name || brand?.name}" ${
          enabled ? "enabled" : "disabled"
        } in ${module.toUpperCase()}`,
        "success"
      );
    } catch (e) {
      console.error("[BRANDS HUB MEMBERSHIP ERROR]", e);
      showToast(e?.message || "Update failed", "error");
      load();
    } finally {
      setBusyId(null);
    }
  };

  // Local styles
  const styles = {
    toolbar: {
      background: "#fff",
      border: "1px solid var(--border)",
      borderRadius: 16,
      padding: 14,
      boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
      display: "flex",
      gap: 10,
      alignItems: "center",
      flexWrap: "wrap",
    },
    tableWrap: {
      border: "1px solid var(--border)",
      borderRadius: 16,
      background: "#fff",
      boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
      overflow: "hidden",
    },
    th: {
      padding: 12,
      textAlign: "left",
      fontSize: 12,
      letterSpacing: 0.2,
      textTransform: "uppercase",
      background: "#0f172a",
      color: "#f9fafb",
      borderBottom: "1px solid rgba(255,255,255,0.12)",
      fontWeight: 650,
      whiteSpace: "nowrap",
      cursor: "pointer",
      userSelect: "none",
    },
    thNoSort: {
      padding: 12,
      textAlign: "left",
      fontSize: 12,
      letterSpacing: 0.2,
      textTransform: "uppercase",
      background: "#0f172a",
      color: "#f9fafb",
      borderBottom: "1px solid rgba(255,255,255,0.12)",
      fontWeight: 650,
      whiteSpace: "nowrap",
    },
    td: {
      padding: 12,
      borderBottom: "1px solid rgba(15,23,42,0.08)",
      verticalAlign: "middle",
      color: "#111827",
      fontWeight: 450,
      fontSize: 14,
    },
    brandCell: { fontWeight: 550 },
    rowOdd: { background: "#fcfcfd" },
    count: {
      marginLeft: "auto",
      fontSize: 12,
      color: "#6b7280",
      fontWeight: 600,
    },
    btnRename: {
      padding: "8px 12px",
      borderRadius: 10,
      border: "1px solid rgba(15,23,42,0.18)",
      background: "#fff",
      color: "#111827",
      fontWeight: 600,
      cursor: "pointer",
    },
    btnDelete: {
      padding: "8px 12px",
      borderRadius: 10,
      border: "1px solid rgba(220,38,38,0.3)",
      background: "#fff",
      color: "#dc2626",
      fontWeight: 600,
      cursor: "pointer",
      marginLeft: 6,
    },
    btnDisabled: { opacity: 0.6, cursor: "not-allowed" },
  };

  return (
    <AppShell
      headerSubtitle={`Brands Management — ${userLabel}`}
      headerRight={
        <UserActionsBar
          mode="module"
          currentUser={currentUser}
          onBackToModules={() => nav("/modules")}
        />
      }
    >
      <header className="page-header">
        <div>
          <h1 className="page-title" style={{ fontWeight: 700 }}>
            Brands Management
          </h1>
          <p className="page-sub">
            One place to control brand access across modules (LLM / Ads).
          </p>
        </div>
      </header>

      <div style={styles.toolbar}>
        <button className="btn-primary" onClick={openCreate} disabled={creating}>
          {creating ? "Creating..." : "Create brand"}
        </button>

        <button className="btn-secondary" onClick={load} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>

        <input
          className="fld"
          style={{ maxWidth: 360 }}
          placeholder="Search brand..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div style={styles.count}>
          {loading ? "Loading..." : `${filtered.length} / ${items.length} brands`}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={styles.tableWrap}>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 720,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{ ...styles.th, width: 90 }}
                    onClick={() => handleSort("id")}
                    title="Sort by ID"
                  >
                    ID
                    <SortArrow column="id" sortCol={sortCol} sortDir={sortDir} />
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort("name")}
                    title="Sort by brand name"
                  >
                    Brand
                    <SortArrow column="name" sortCol={sortCol} sortDir={sortDir} />
                  </th>
                  <th style={{ ...styles.thNoSort, width: 140 }} title="Brand country">
                    Country
                  </th>
                  <th
                    style={{ ...styles.th, width: 180 }}
                    onClick={() => handleSort("llm")}
                    title="Sort by LLM status"
                  >
                    LLM
                    <SortArrow column="llm" sortCol={sortCol} sortDir={sortDir} />
                  </th>
                  <th
                    style={{ ...styles.th, width: 180 }}
                    onClick={() => handleSort("ads")}
                    title="Sort by Ads status"
                  >
                    Ads
                    <SortArrow column="ads" sortCol={sortCol} sortDir={sortDir} />
                  </th>
                  <th style={{ ...styles.thNoSort, width: 300 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((b, idx) => {
                  const id = Number(b.id);
                  const busy = busyId === id;

                  return (
                    <tr key={id} style={idx % 2 === 0 ? styles.rowOdd : undefined}>
                      <td style={{ ...styles.td, width: 90, fontWeight: 650 }}>
                        {id}
                      </td>

                      <td style={{ ...styles.td, ...styles.brandCell }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span>{b.name}</span>
                          {activeByBrand[id]?.count > 0 && (
                            <ActiveRunsChip
                              count={activeByBrand[id].count}
                              runs={activeByBrand[id].runs}
                              open={activePopoverBrandId === id}
                              onToggle={() =>
                                setActivePopoverBrandId((prev) => (prev === id ? null : id))
                              }
                              onClose={() => setActivePopoverBrandId(null)}
                              onOpenRun={(runId) => {
                                setActivePopoverBrandId(null);
                                nav(`/brands/${id}/new-analysis?runId=${runId}`);
                              }}
                            />
                          )}
                        </div>
                      </td>

                      <td style={{ ...styles.td, width: 140, color: b.country ? undefined : "#9ca3af" }}>
                        {b.country || "\u2014"}
                      </td>

                      <td style={{ ...styles.td, width: 180 }}>
                        <Toggle
                          label={b.modules?.llm ? "Enabled" : "Disabled"}
                          checked={!!b.modules?.llm}
                          disabled={busy}
                          onChange={(val) => setMembership(b, "llm", val)}
                        />
                      </td>

                      <td style={{ ...styles.td, width: 180 }}>
                        <Toggle
                          label={b.modules?.ads ? "Enabled" : "Disabled"}
                          checked={!!b.modules?.ads}
                          disabled={busy}
                          onChange={(val) => setMembership(b, "ads", val)}
                        />
                      </td>

                      <td style={{ ...styles.td, width: 300 }}>
                        <button
                          onClick={() => nav(`/brands/${b.id}/new-analysis`)}
                          disabled={busy || !b.modules?.llm}
                          title={
                            !b.modules?.llm
                              ? "Enable the LLM module to start an analysis"
                              : `Start a new analysis for ${b.name}`
                          }
                          style={{
                            ...styles.btnRename,
                            ...(busy || !b.modules?.llm ? styles.btnDisabled : null),
                            background: "#111827",
                            color: "#d4a017",
                            borderColor: "#d4a017",
                            fontWeight: 900,
                          }}
                        >
                          + New Analysis
                        </button>
                        <button
                          onClick={() => setEditSetupBrandId(b.id)}
                          disabled={busy}
                          style={{
                            ...styles.btnRename,
                            ...(busy ? styles.btnDisabled : null),
                            background: "#ecfdf5",
                            color: "#166534",
                            borderColor: "#bbf7d0",
                          }}
                        >
                          Edit Setup
                        </button>
                        <button
                          onClick={() => nav(`/brands/${b.id}/questions`)}
                          disabled={busy || !b.modules?.llm}
                          title={
                            !b.modules?.llm
                              ? "Enable the LLM module to manage questions"
                              : `Manage ${b.name}'s question library`
                          }
                          style={{
                            ...styles.btnRename,
                            ...(busy || !b.modules?.llm ? styles.btnDisabled : null),
                            background: "#eff6ff",
                            color: "#1e3a8a",
                            borderColor: "#bfdbfe",
                          }}
                        >
                          Questions
                        </button>
                        <button
                          onClick={() => openRename(b)}
                          disabled={busy}
                          style={{
                            ...styles.btnRename,
                            ...(busy ? styles.btnDisabled : null),
                          }}
                        >
                          Rename
                        </button>
                        <button
                          onClick={() => openAliases(b)}
                          disabled={busy}
                          style={{
                            ...styles.btnRename,
                            ...(busy ? styles.btnDisabled : null),
                            background: "#ede9fe",
                            color: "#6d28d9",
                            borderColor: "#c4b5fd",
                          }}
                        >
                          Aliases
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => openDelete(b)}
                            disabled={busy}
                            style={{
                              ...styles.btnDelete,
                              ...(busy ? styles.btnDisabled : null),
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!loading && filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      style={{
                        ...styles.td,
                        padding: 14,
                        color: "#6b7280",
                        fontWeight: 450,
                      }}
                    >
                      No brands match your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Toast
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast(null)}
      />

      {/* Create brand — onboarding wizard */}
      <BrandOnboardingModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (brand) => {
          await load();
          showToast(`Brand "${brand?.brand?.name || "new brand"}" created`, "success");
          setCreateOpen(false);
        }}
        defaultEnableLlm={true}
        defaultEnableAds={false}
      />

      {/* Edit brand setup wizard */}
      <BrandOnboardingModal
        open={!!editSetupBrandId}
        onClose={() => setEditSetupBrandId(null)}
        editBrandId={editSetupBrandId}
        onCreated={async () => {
          await load();
          showToast("Brand setup updated", "success");
          setEditSetupBrandId(null);
        }}
      />

      {/* Rename dialog */}
      <ConfirmDialog
        open={renameOpen}
        title="Rename brand"
        message={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ color: "#374151", fontSize: 13 }}>
              Renaming affects both modules. IDs stay the same.
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Current:{" "}
              <span style={{ fontWeight: 650, color: "#111827" }}>
                {renameTarget?.name}
              </span>
            </div>
          </div>
        }
        confirmText={renaming ? "Saving..." : "Save"}
        cancelText="Cancel"
        showInput
        defaultValue={renameTarget?.name || ""}
        onConfirm={handleRename}
        onCancel={() => setRenameOpen(false)}
      />

      {/* Delete confirmation dialog (admin only) */}
      <ConfirmDialog
        open={deleteOpen}
        title="Delete brand"
        message={
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ color: "#dc2626", fontSize: 13, fontWeight: 600 }}>
              This will permanently remove the brand from all modules.
            </div>
            <div style={{ fontSize: 13, color: "#374151" }}>
              Brand:{" "}
              <span style={{ fontWeight: 650, color: "#111827" }}>
                {deleteTarget?.name}
              </span>{" "}
              (ID: {deleteTarget?.id})
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              This action cannot be undone.
            </div>
          </div>
        }
        confirmText={deleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
      />

      {/* Aliases dialog */}
      {aliasOpen && aliasTarget && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 200,
          background: "rgba(0,0,0,0.4)", display: "grid", placeItems: "center", padding: 20,
        }} onClick={() => setAliasOpen(false)}>
          <div style={{
            background: "#fff", borderRadius: 14, padding: 24, maxWidth: 520, width: "100%",
            maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1a1f36" }}>
              LLM Discovery Brand Aliases
            </h3>
            <p style={{ margin: "4px 0 16px", fontSize: 13, color: "#6b7280" }}>
              Aliases for <strong>{aliasTarget.name}</strong> — used by Python metrics and MAD Pilot for brand matching.
            </p>

            {/* Add alias input */}
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input
                style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 10px", fontSize: 13 }}
                placeholder="Add alias (e.g., BRD Expresso)"
                value={aliasInput}
                onChange={(e) => setAliasInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddAlias()}
              />
              <button className="btn-primary" style={{ padding: "7px 16px", fontSize: 13 }} onClick={handleAddAlias}>
                Add
              </button>
            </div>

            {/* Alias list */}
            {aliasLoading ? (
              <p style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</p>
            ) : aliases.length === 0 ? (
              <p style={{ color: "#9ca3af", fontSize: 13, textAlign: "center", padding: 16 }}>
                No aliases configured. Add aliases so Python metrics can match brand variants like "BRD Expresso", "Creditul Expresso", etc.
              </p>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {aliases.map((a) => (
                  <span key={a.id} style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 10px", background: "#ede9fe", color: "#6d28d9",
                    borderRadius: 6, fontSize: 13, fontWeight: 500,
                  }}>
                    {a.alias}
                    <button
                      onClick={() => handleDeleteAlias(a.id)}
                      style={{ background: "none", border: "none", color: "#a78bfa", cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}
                      title="Remove alias"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div style={{ marginTop: 16, textAlign: "right" }}>
              <button className="btn-secondary" style={{ padding: "7px 16px", fontSize: 13 }} onClick={() => setAliasOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
